import React from 'react';
import { Avatar, Card, Progress, Stats, Table } from '../../components/ui.jsx';
import { entryHours, fmtDur } from '../../lib/time.js';
import { ReportRangeFilter } from '../../components/reporting/ReportFilters.jsx';
import {
  useReportData,
  useReportFilters,
} from '../../components/reporting/useReportFilters.js';

// The original project-detail body: period filter, budget stats, team
// contribution and recent entries. This is the effort view — unchanged in
// substance, just lifted into its own tab.
export function ProjectTrackerTab({ project, data }) {
  const filters = useReportFilters(data, { scope: { projectId: project.id } });
  const { report, loading } = useReportData(filters.query);
  // An absence project's own time is absence, which reports keep out of
  // hours — show it here, it is all this project has.
  const byUser = project.absence
    ? report.people
        .map((row) => ({ ...row, hours: row.absenceHours }))
        .sort((a, b) => b.hours - a.hours)
    : report.people;
  const periodHours = project.absence
    ? report.summary.absenceHours
    : report.summary.totalHours;
  const periodLabel = `${filters.from} to ${filters.to}`;
  const recentEntries = report.entries.slice(0, 8);
  const hasBudget = project.budgetMD > 0;
  const pct = hasBudget ? (project.spentMD / project.budgetMD) * 100 : 0;

  return (
    <>
      <ReportRangeFilter {...filters} />
      <Stats
        cards={[
          [
            'Budget',
            hasBudget ? `${project.budgetMD} MD` : 'Not set',
            hasBudget ? `${project.budgetMD * 8} hours` : '',
          ],
          [
            project.absence ? 'Absence in period' : 'Spent in period',
            `${(periodHours / 8).toFixed(1)} MD`,
            `${fmtDur(periodHours)} · ${periodLabel}${loading ? ' · Loading...' : ''}`,
          ],
          [
            'Remaining',
            hasBudget
              ? `${(project.budgetMD - project.spentMD).toFixed(1)} MD`
              : 'N/A',
            hasBudget ? 'all-time budget position' : 'no budget set',
          ],
          [
            'Burn rate',
            hasBudget ? `${pct.toFixed(0)}%` : 'N/A',
            hasBudget
              ? pct > 85
                ? 'needs attention'
                : 'healthy (all-time)'
              : 'no budget set',
          ],
        ]}
      />
      <div className="detail-main">
        <Card title={`Team contribution · ${periodLabel}`}>
          {byUser.length === 0 && (
            <p className="muted-text">No logged time in this period.</p>
          )}
          {byUser.map(({ user, hours }) => (
            <div className="contribution" key={user.id || user.name}>
              <Avatar user={user} />
              <span>{user.name}</span>
              <Progress pct={(hours / Math.max(1, byUser[0]?.hours)) * 100} />
              <strong>{fmtDur(hours)}</strong>
            </div>
          ))}
        </Card>
        <Card title="Entries in period · latest 8">
          <Table headers={['Date', 'User', 'Description', 'Hours']}>
            {recentEntries.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.workDate}</td>
                <td>{entry.userName}</td>
                <td>
                  <span className="truncate table-description">
                    {entry.description}
                  </span>
                </td>
                <td className="strong">{fmtDur(entryHours(entry))}</td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>
    </>
  );
}
